import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Button, Row, Col } from "reactstrap";
import ataDefinitionService from "services/AtaDefinicoesService";
import ModalExcludeDefinition from "./ModalExcludeDefinition";
import ModalCreateEditDefinition from "./ModalCreateEditDefinition";
import moment from "moment";
import Loading from "components/Common/Loading";
import DynamicMaterialTable from "components/Common/MaterialTableContainer";
import useTableZoom from "components/Common/useTableZoom";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { IconButton, Tooltip } from "@mui/material";
import {
	getTableBodyCellProps,
	getTableHeaderCellProps,
} from "components/Common/MaterialTableStyles";

const DefinicoesAta = (props) => {
	// ========== CONFIGURAÇÃO DE LARGURAS MÍNIMAS E MÁXIMAS DAS COLUNAS ==========
	const COLUMN_WIDTHS = {
		included_at: { min: 100, max: 100 },   // Largura mín/máx da coluna "Inserido" (em pixels)
		definition: { min: 200 },              // Largura mínima da coluna "Definição" (sem máximo - cresce livremente)
		actions: { min: 60, max: 60 },         // Largura mín/máx da coluna "Actions" (em pixels)
	};
	// ================================================================

	const [data, setData] = useState([]);
	const [modal, setModal] = useState(null);
	const [showModal, setShowModal] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const id = props?.dataId;
	const podeVisualizar = props?.podeVisualizar;

	// Usar o hook customizado para zoom e cálculos de largura
	const { zoomLevel, getColumnWidth } = useTableZoom(COLUMN_WIDTHS);

	const columns = useMemo(
		() => [
			{
				header: "Inserido",
				accessorKey: "included_at",
				filterFn: "contains",
				widthPixels: getColumnWidth("included_at"),
				minWidthPixels: COLUMN_WIDTHS.included_at.min,
				maxWidthPixels: COLUMN_WIDTHS.included_at.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("firstColumn"),
				Cell: ({ cell }) => (
					<span style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						width: "100%",
						height: "100%",
					}}>
						{moment(cell.getValue()).format("DD/MM/YYYY")}
					</span>
				),
			},
			{
				header: "Definição",
				accessorKey: "definition",
				filterFn: "contains",
				widthPixels: getColumnWidth("definition"),
				minWidthPixels: COLUMN_WIDTHS.definition.min,
				// Não tem maxWidthPixels - pode crescer livremente
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
				Cell: ({ cell, row }) => (
					<>
						{podeVisualizar ? (
							<span
								style={{
									cursor: "pointer",
									display: "block", // Permite que o texto quebre corretamente
									maxWidth: "100%", // Respeita o tamanho da célula
									wordBreak: "break-word", // Quebra o texto se for muito grande
									overflowWrap: "break-word", // Compatibilidade extra para quebra de palavras
									padding: "3px",
									lineHeight: "1.2",
								}}
								onMouseEnter={(e) => {
									e.target.style.textDecoration = "underline";
								}}
								onMouseLeave={(e) => {
									e.target.style.textDecoration = "none";
								}}
								onClick={() => editarItem(row.original)}
							>
								{cell.getValue()}
							</span>
						) : (
							<span
								style={{
									display: "block", // Permite que o texto quebre corretamente
									maxWidth: "100%", // Respeita o tamanho da célula
									wordBreak: "break-word", // Quebra o texto se for muito grande
									overflowWrap: "break-word", // Compatibilidade extra para quebra de palavras
									padding: "3px",
									lineHeight: "1.2",
								}}
							>
								{cell.getValue()}
							</span>
						)}
					</>
				),
			},
			{
				accessorKey: "actions",
				widthPixels: getColumnWidth("actions"),
				minWidthPixels: COLUMN_WIDTHS.actions.min,
				maxWidthPixels: COLUMN_WIDTHS.actions.max,
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("actionColumn"),
				Cell: ({ row }) => (
					<>
						{podeVisualizar && (
							<div style={{ 
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								width: "100%",
								height: "100%",
							}}>
								<div>
									<Tooltip
										title="Excluir"
										placement="top"
									>
										<IconButton
											style={{
												background: "none",
												border: "none",
												padding: "1px",
												cursor: "pointer",
												color: "black",
												minWidth: "auto",
												width: "auto",
												height: "auto",
											}}
											onClick={() =>
												excluirItem(row.original)
											}
										>
											<Trash size={14} />
										</IconButton>
									</Tooltip>
								</div>
							</div>
						)}
					</>
				),
				align: "center",
			},
		],
		[podeVisualizar, zoomLevel], // Adicionado zoomLevel como dependência
	);

	const editarItem = (rowData) => {
		setModal(rowData);
		setShowEditModal(true);
	};

	const excluirItem = (rowData) => {
		setModal(rowData);
		setShowModal(true);
	};

	function onCloseModal() {
		setShowModal(false);
	}

	function onCloseEditModal() {
		setShowEditModal(false);
	}

	const getItems = async () => {
		try {
			setIsLoading(true);
			const response = await ataDefinitionService.getById(id);
			setData(response);
			setIsLoading(false);
		} catch (error) {
			console.log(error);
		}
	};

	useEffect(() => {
		getItems();
	}, []);

	useEffect(() => {
		getItems();
	}, [showModal, showEditModal]);

	return (
		<Loading isLoading={isLoading}>
			<div>
				<Row>
					<Col
						md={12}
						style={{
							display: "flex",
							justifyContent: "end",
							marginTop: 10,
							marginBottom: 10,
						}}
					>
						{podeVisualizar && (
							<Button
								onClick={() => editarItem(id)}
								style={{
									background: "none",
									padding: 10,
									cursor: "pointer",
									color: "black",
									marginLeft: 10,
								}}
							>
								<Plus size={16} /> Nova Definição
							</Button>
						)}
					</Col>
				</Row>
				<div style={{ marginTop: 10 }} />
				<DynamicMaterialTable columns={columns} data={data} />

				<ModalExcludeDefinition
					show={showModal}
					onCloseClick={onCloseModal}
					data={modal}
				/>

				<ModalCreateEditDefinition
					show={showEditModal}
					onCloseClick={onCloseEditModal}
					data={modal}
				/>
			</div>
		</Loading>
	);
};

DefinicoesAta.propTypes = {
	preGlobalFilteredRows: PropTypes.any,
};

export default DefinicoesAta;
